/**
 * -----------------------------------------------------------------------------
 * Syncfusion Dialog – Body Scroll Lock Patch
 * -----------------------------------------------------------------------------
 *
 * Library:
 *   @syncfusion/ej2-angular-popups ^32.1.24
 *
 * Context:
 *   O componente ejs-dialog da Syncfusion aplica e remove classes globais
 *   (e-dlg-target e e-scroll-disabled) no document.body para controlar o
 *   bloqueio de scroll quando dialogs modais estão abertos.
 *
 * Problema:
 *   Ao fechar um dialog modal, a implementação atual da Syncfusion remove
 *   essas classes do document.body de forma incondicional, mesmo quando ainda
 *   existe outro dialog modal aberto (ex.: dialogs aninhados ou múltiplos
 *   dialogs ativos).
 *
 *   Esse comportamento resulta em:
 *     - liberação indevida do scroll do body
 *     - exibição intermitente da scrollbar
 *     - flicker visual e quebra de layout
 *
 * Motivo do patch:
 *   A biblioteca não mantém contagem de dialogs modais abertos (ref-count) e
 *   não expõe hooks públicos para interceptar esse ponto do ciclo de vida.
 *
 * Solução adotada:
 *   Este patch intercepta de forma cirúrgica a remoção das classes de scroll
 *   no document.body, permitindo o unlock somente quando não existir nenhum
 *   outro dialog modal aberto além do dialog atualmente em fechamento.
 *
 *   A abordagem:
 *     - evita flicker
 *     - não altera node_modules
 *     - não modifica a API pública da Syncfusion
 *     - não interfere em outros elementos ou bibliotecas
 *
 * Observação:
 *   Este patch deve ser aplicado uma única vez, preferencialmente antes do
 *   bootstrap da aplicação Angular (ex.: em main.ts).
 * -----------------------------------------------------------------------------
 */

import { Dialog, Popup } from '@syncfusion/ej2-popups';

const DLG_TARGET = 'e-dlg-target';
const SCROLL_DISABLED = 'e-scroll-disabled';
const ROOT = 'e-dialog';

let openModalCount = 0;

function isLockToken(token: string): boolean {
  return token === DLG_TARGET || token === SCROLL_DISABLED;
}

export function patchSyncfusionDialogBodyUnlock() {
  // ---------------------------
  // (1) Patch no Dialog.show: incrementa contador ao abrir modal
  // ---------------------------
  const dialogProto = (Dialog as any).prototype;
  const popupProto = (Popup as any).prototype;
  
  // Garante que o patch seja aplicado apenas uma vez
  if (!dialogProto.__smoShowCountPatchApplied) {
    dialogProto.__smoShowCountPatchApplied = true;

    const originalShow = dialogProto.show;

    dialogProto.show = function (...args: any[]) {
      openModalCount++;
      console.debug(`[Syncfusion Dialog Patch] openModalCount incremented to ${openModalCount}`);
      return originalShow.apply(this, args);
    };
  }

  // Gantante que o patch seja aplicado apenas uma vez
  if (!popupProto.__smoHidePatchApplied) {
    popupProto.__smoHidePatchApplied = true;
    const originalHide = popupProto.hide;

    popupProto.hide = function (...args: any[]) {
      // Chama o hide original (que eventualmente tentará remover as classes do body)
      if (this.element && this.element.classList.contains(ROOT)) {
        openModalCount--;
        console.debug(`[Syncfusion Dialog Patch] openModalCount decremented to ${openModalCount}`, this);
      }
      return originalHide.apply(this, args);
    };
  }

  // ---------------------------
  // (2) Patch no classList.remove do BODY: decrementa e só libera se count==0
  // ---------------------------
  const tokenProto = (DOMTokenList as any).prototype;

  if (tokenProto.__smoBodyRemoveCountPatchApplied) return;
  tokenProto.__smoBodyRemoveCountPatchApplied = true;

  const originalRemove = tokenProto.remove;

  tokenProto.remove = function (...tokens: string[]) {
    // Intercepta SOMENTE o classList do BODY
    if (this === document.body.classList) {
      const removingLock = tokens.some(isLockToken);

      if (removingLock) {
        // Ainda existem modais abertos → bloqueia remoção do lock
        if (openModalCount > 0) {
          // Se vieram outras classes junto, remove só as outras
          const filtered = tokens.filter(t => !isLockToken(t));
          if (filtered.length > 0) {
            return originalRemove.apply(this, filtered);
          }
          return; // bloqueia completamente a remoção do lock
        }

        // Se não tem mais modais abertos → permite remover normalmente
        return originalRemove.apply(this, tokens);
      }
    }

    // Caso geral: comportamento padrão
    return originalRemove.apply(this, tokens);
  };
}
